import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';

/**
 * Context for CMMS-specific authentication and permissions
 */
const CmmsAuthContext = createContext(null);

/**
 * Storage keys for CMMS auth data
 */
const STORAGE_KEYS = {
  CMMS_USER: 'cmmsUser',
  CMMS_PERMISSIONS: 'cmmsPermissions',
};

/**
 * CmmsAuthProvider - Provides CMMS user info and permissions to the app
 */
export const CmmsAuthProvider = ({ children }) => {
  const [cmmsUser, setCmmsUser] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch CMMS user data and permissions from the backend
   */
  const fetchCmmsUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user from main auth
      const user = authService.getCurrentUser();
      const companyId = authService.getCompanyId();

      if (!user || !companyId) {
        console.log('No user or company ID found, skipping CMMS auth');
        setLoading(false);
        return;
      }

      // Skip CMMS auth entirely if the company has no CMMS subscription
      const hasCmmsSubscription = localStorage.getItem('hasCmmsSubscription') === 'true';
      if (!hasCmmsSubscription) {
        setLoading(false);
        return;
      }

      // Fetch CMMS user with permissions from backend
      const response = await apiClient.get(
        `/api/cmms/team/me?companyId=${companyId}&email=${encodeURIComponent(user.email)}`
      );

      if (response) {
        setCmmsUser(response);
        setPermissions(new Set(response.permissions || []));

        // Store in localStorage for quick access
        localStorage.setItem(STORAGE_KEYS.CMMS_USER, JSON.stringify(response));
        localStorage.setItem(
          STORAGE_KEYS.CMMS_PERMISSIONS,
          JSON.stringify(response.permissions || [])
        );

        console.log('CMMS user loaded:', response.email, 'Role:', response.role);
        console.log('Permissions:', response.permissions?.length || 0);
      }
    } catch (err) {
      console.error('Failed to fetch CMMS user:', err);
      setError(err.message);

      // Try to load from localStorage as fallback
      const cachedUser = localStorage.getItem(STORAGE_KEYS.CMMS_USER);
      const cachedPerms = localStorage.getItem(STORAGE_KEYS.CMMS_PERMISSIONS);

      if (cachedUser) {
        setCmmsUser(JSON.parse(cachedUser));
        setPermissions(new Set(JSON.parse(cachedPerms || '[]')));
        console.log('Loaded CMMS user from cache');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    fetchCmmsUser();
  }, [fetchCmmsUser]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission) => {
      return permissions.has(permission);
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback(
    (...perms) => {
      return perms.some((p) => permissions.has(p));
    },
    [permissions]
  );

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback(
    (...perms) => {
      return perms.every((p) => permissions.has(p));
    },
    [permissions]
  );

  /**
   * Refresh CMMS user data
   */
  const refreshCmmsUser = useCallback(async () => {
    await fetchCmmsUser();
  }, [fetchCmmsUser]);

  /**
   * Clear CMMS auth data (on logout)
   */
  const clearCmmsAuth = useCallback(() => {
    setCmmsUser(null);
    setPermissions(new Set());
    localStorage.removeItem(STORAGE_KEYS.CMMS_USER);
    localStorage.removeItem(STORAGE_KEYS.CMMS_PERMISSIONS);
  }, []);

  const value = {
    cmmsUser,
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshCmmsUser,
    clearCmmsAuth,
  };

  return <CmmsAuthContext.Provider value={value}>{children}</CmmsAuthContext.Provider>;
};

/**
 * Hook to use CMMS auth context
 */
export const useCmmsAuth = () => {
  const context = useContext(CmmsAuthContext);
  if (!context) {
    throw new Error('useCmmsAuth must be used within a CmmsAuthProvider');
  }
  return context;
};

/**
 * HOC to wrap components with CMMS auth requirement
 */
export const withCmmsAuth = (Component) => {
  return function WrappedComponent(props) {
    const cmmsAuth = useCmmsAuth();
    return <Component {...props} cmmsAuth={cmmsAuth} />;
  };
};

export default CmmsAuthContext;
